import { cn } from '@/lib/utils';
import { type LucideIcon, MessageSquare, Network, Terminal } from 'lucide-react';

export type MobileView = 'chat' | 'graph' | 'terminal';

interface MobileTabBarProps {
  currentView: MobileView;
  onViewChange: (view: MobileView) => void;
  className?: string;
}

interface Tab {
  id: MobileView;
  label: string;
  icon: LucideIcon;
}

const TABS: Tab[] = [
  { id: 'chat', label: 'Chat', icon: MessageSquare },
  { id: 'graph', label: 'Graph', icon: Network },
  { id: 'terminal', label: 'Terminal', icon: Terminal }
];

/**
 * Bottom tab bar for the mobile layout: one tap switches between the Chat,
 * Graph and Terminal panels. Theme-aware (semantic tokens) and touch-sized.
 */
export default function MobileTabBar({
  currentView,
  onViewChange,
  className
}: MobileTabBarProps) {
  return (
    <nav
      role="tablist"
      aria-label="Panel switcher"
      className={cn(
        'flex items-stretch border-t border-border bg-card',
        'pb-[env(safe-area-inset-bottom)]',
        className
      )}
    >
      {TABS.map((tab) => {
        const Icon = tab.icon;
        const isActive = currentView === tab.id;

        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-label={`${tab.label} panel`}
            onClick={() => onViewChange(tab.id)}
            className={cn(
              'relative flex flex-1 flex-col items-center justify-center gap-1',
              'h-14 touch-manipulation transition-colors',
              isActive
                ? 'text-primary'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            {isActive && (
              <span className="absolute inset-x-4 top-0 h-0.5 rounded-full bg-primary" />
            )}
            <Icon className="h-5 w-5" />
            <span className="text-xs font-medium">{tab.label}</span>
          </button>
        );
      })}
    </nav>
  );
}
